import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';

interface PaymentGatewayProps {
  amount: number;
  plan: string;
  billingCycle: string;
  email: string;
  phone?: string;
  userId: string;
    invoiceId?: string;   
  // Pass these in from Billing.tsx so the gateway knows a card is already saved
  savedCardLast4?: string;
  savedCardBrand?: string;
  savedCardExpiry?: string;
  stripePaymentMethodId?: string;
  stripeCustomerId?: string;
  // Coupon support is Paynow-only (see modules/coupons.js) — paynowAmount is
  // the coupon-discounted price for the Paynow button specifically; every
  // other method below keeps charging the full `amount`, unaffected.
  couponCode?: string;
  paynowAmount?: number;
  onSuccess: () => void;
  onError: (error: string) => void;
}

declare global {
  interface Window {
    Stripe: any;
    paypal: any;
  }
}

const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

export default function PaymentGateway({
  amount,
  plan,
  billingCycle,
  email,
  phone,
  userId,
  invoiceId,
  savedCardLast4,
  savedCardBrand,
  savedCardExpiry,
  stripePaymentMethodId,
  stripeCustomerId,
  couponCode,
  paynowAmount,
  onSuccess,
  onError,
}: PaymentGatewayProps) {
  const hasSavedCard = !!stripePaymentMethodId && amount > 0;
  const paynowChargeAmount = paynowAmount ?? amount;

  const [methodsEnabled, setMethodsEnabled] = useState<{ stripe: boolean; paynow: boolean; paypal: boolean; dpo: boolean; bankTransfer: boolean; bankTransferDetails: any }>({ stripe: true, paynow: true, paypal: true, dpo: false, bankTransfer: false, bankTransferDetails: null });
  const [selectedGateway, setSelectedGateway] = useState<'paynow' | 'stripe' | 'paypal' | 'dpo' | 'bankTransfer'>('stripe');
  // When a saved card exists and amount > 0, default to using it
  const [useNewCard, setUseNewCard] = useState(!hasSavedCard);
  const [bankTransferLoading, setBankTransferLoading] = useState(false);
  const [bankTransferDone, setBankTransferDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [paypalReady, setPaypalReady] = useState(false);
  const [cardError, setCardError] = useState('');
  const [paynowPhone, setPaynowPhone] = useState(phone || '');
  const [paynowMethod, setPaynowMethod] = useState<'ecocash' | 'onemoney' | 'web'>('web');

  const stripeRef = useRef<any>(null);
  const cardElementRef = useRef<any>(null);
  const cardMountRef = useRef<HTMLDivElement>(null);
  const paypalContainerRef = useRef<HTMLDivElement>(null);
  const paypalRenderedRef = useRef(false);

  // ─── Fetch enabled payment methods ───────────────────────────────────────
  useEffect(() => {
    apiFetch('/api/payment-methods-config')
      .then(r => r.json())
      .then(data => {
        setMethodsEnabled(data);
        // If current selection is disabled, switch to first enabled method
        setSelectedGateway(g => {
          if (data[g]) return g;
          return (['stripe', 'paynow', 'paypal', 'dpo', 'bankTransfer'] as const).find(m => data[m]) || 'stripe';
        });
      })
      .catch(() => {}); // keep defaults (all enabled) on error
  }, []);

  // ─── Load Stripe.js ───────────────────────────────────────────────────────
  useEffect(() => {
    if (window.Stripe) { initStripe(); return; }
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = initStripe;
    document.head.appendChild(script);
  }, []);

  function initStripe() {
    const pk = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!pk) { console.error('REACT_APP_STRIPE_PUBLISHABLE_KEY not set'); return; }
    stripeRef.current = window.Stripe(pk);
    setStripeReady(true);
  }

  // ─── Mount Stripe Card Element only when entering new card ────────────────
  useEffect(() => {
    if (selectedGateway !== 'stripe' || !stripeReady || !cardMountRef.current) return;
    if (!useNewCard) return; // don't mount if using saved card
    if (cardElementRef.current) return;

    const elements = stripeRef.current.elements();
    const card = elements.create('card', {
      style: {
        base: {
          fontSize: '16px',
          color: '#1f2937',
          '::placeholder': { color: '#9ca3af' },
          fontFamily: 'system-ui, sans-serif',
        },
        invalid: { color: '#ef4444' },
      },
      hidePostalCode: false,
    });
    card.mount(cardMountRef.current);
    card.on('change', (event: any) => {
      setCardError(event.error ? event.error.message : '');
    });
    cardElementRef.current = card;

    return () => {
      card.unmount();
      cardElementRef.current = null;
    };
  }, [selectedGateway, stripeReady, useNewCard]);

  // ─── Load & render PayPal button ─────────────────────────────────────────
  useEffect(() => {
    if (selectedGateway !== 'paypal') return;
    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    if (!clientId) { console.error('REACT_APP_PAYPAL_CLIENT_ID not set'); return; }

    const renderPayPal = () => {
      if (!paypalContainerRef.current || paypalRenderedRef.current) return;
      paypalRenderedRef.current = true;
      window.paypal.Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' },
        createOrder: async () => {
          const res = await apiFetch('/api/paypal/create-order', {
            method: 'POST',
            body: JSON.stringify({ amount, plan, billingCycle, email }),
          });
          const data = await res.json();
          if (!data.success) throw new Error(data.error || 'Failed to create order');
          return data.orderId;
        },
        onApprove: async (data: any) => {
          setLoading(true);
          try {
            const res = await apiFetch('/api/paypal/capture-order', {
              method: 'POST',
              body: JSON.stringify({ orderId: data.orderID, plan, billingCycle }),
            });
            const result = await res.json();
            if (result.success) onSuccess();
            else onError(result.error || 'Capture failed');
          } catch (err: any) {
            onError(err.message);
          } finally {
            setLoading(false);
          }
        },
        onError: (err: any) => onError('PayPal error: ' + err.message),
      }).render(paypalContainerRef.current);
    };

    if (window.paypal) { renderPayPal(); setPaypalReady(true); return; }
    const existingScript = document.querySelector('script[data-paypal]');
    if (existingScript) existingScript.remove();
    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.setAttribute('data-paypal', 'true');
    script.onload = () => { setPaypalReady(true); renderPayPal(); };
    document.head.appendChild(script);
    return () => { paypalRenderedRef.current = false; };
  }, [selectedGateway]);

  // ─── Charge saved card (off-session via server) ───────────────────────────
  const handleSavedCardPayment = async () => {
    setLoading(true);
    setCardError('');
    try {
      const res = await apiFetch('/api/stripe/charge-saved-card', {
        method: 'POST',
        body: JSON.stringify({
          amount,
          plan,
          billingCycle,
          email,
          paymentMethodId: stripePaymentMethodId,
          customerId: stripeCustomerId,
          ...(invoiceId ? { invoiceId } : {}),
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Payment failed');
      onSuccess();
    } catch (err: any) {
      setCardError(err.message);
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── New card payment ─────────────────────────────────────────────────────
  const handleStripePayment = async () => {
    if (!stripeRef.current || !cardElementRef.current) return;
    setLoading(true);
    setCardError('');
    try {
      const intentRes = await apiFetch('/api/stripe/create-payment-intent', {
        method: 'POST',
        body: JSON.stringify({ amount, plan, billingCycle, email, userId }),
      });
      const intentData = await intentRes.json();
      if (!intentData.success) throw new Error(intentData.error || 'Failed to create intent');

      if (intentData.isSetupIntent) {
        const { error, setupIntent } = await stripeRef.current.confirmCardSetup(intentData.clientSecret, {
          payment_method: { card: cardElementRef.current, billing_details: { email } },
        });
        if (error) throw new Error(error.message);
        const confirmRes = await apiFetch('/api/stripe/save-card', {
          method: 'POST',
          body: JSON.stringify({
            setupIntentId: setupIntent.id,
            paymentMethodId: setupIntent.payment_method,
            userId,
          }),
        });
        const confirmData = await confirmRes.json();
        if (!confirmData.success) throw new Error(confirmData.error || 'Failed to save card');
      } else {
        const { error, paymentIntent } = await stripeRef.current.confirmCardPayment(intentData.clientSecret, {
          payment_method: { card: cardElementRef.current, billing_details: { email } },
        });
        if (error) throw new Error(error.message);
        if (paymentIntent.status !== 'succeeded') throw new Error('Payment not completed');
        const confirmRes = await apiFetch('/api/stripe/confirm-payment', {
          method: 'POST',
          body: JSON.stringify({ paymentIntentId: paymentIntent.id, plan, billingCycle, userId, ...(invoiceId ? { invoiceId } : {}) }),
        });
        const confirmData = await confirmRes.json();
        if (!confirmData.success) throw new Error(confirmData.error || 'Server confirmation failed');
      }
      onSuccess();
    } catch (err: any) {
      setCardError(err.message);
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ─── PayNow ───────────────────────────────────────────────────────────────
 const handlePaynowPayment = async () => {
  const isTestMode = process.env.REACT_APP_PAYMENT_GATEWAY === 'test';
  const isRedirect = paynowMethod === 'web';

  // The redirect method doesn't collect a phone number at all — Paynow's own
  // hosted page handles method selection (card, EcoCash, etc.) on their side.
  if (!isRedirect) {
    if (!isTestMode && !paynowPhone.trim()) {
      onError('Please enter your mobile money number');
      return;
    }

    // Only validate phone number in live mode
    if (!isTestMode) {
      const phoneRegex = paynowMethod === 'ecocash'
        ? /^(07|2637)[0-9]{8}$/
        : /^(078|26378)[0-9]{8}$/;

      if (!phoneRegex.test(paynowPhone.replace(/\s/g, ''))) {
        onError(`Please enter a valid ${paynowMethod === 'ecocash' ? 'EcoCash' : 'OneMoney'} number`);
        return;
      }
    }
  }

  setLoading(true);
  try {
    const res = await apiFetch('/api/paynow/create', {
      method: 'POST',
      body: JSON.stringify({
        amount: paynowChargeAmount,
        email,
        ...(isRedirect ? {} : { phone: paynowPhone.replace(/\s/g, '') }),
        plan,
        billingCycle,
        userId,
        method: paynowMethod,
        ...(invoiceId ? { invoiceId } : {}),
        ...(couponCode ? { couponCode } : {}),
      }),
    });
    const data = await res.json();

    if (!data.success) throw new Error(data.error || 'Payment creation failed');

    // Show test mode message if applicable
    if (data.isTestMode && data.message) {
      alert(data.message);
    }

    // The server recomputes the coupon discount authoritatively and returns
    // the amount it actually charged — that's what polling/local state needs
    // from here on, not whatever this client-side preview happened to say.
    const chargedAmount = data.chargedAmount ?? paynowChargeAmount;

    if (data.redirectUrl) {
      // Save poll data before leaving so Billing page can verify on return
      if (data.pollUrl) {
        localStorage.setItem('paynow_pending', JSON.stringify({
          pollUrl: data.pollUrl, reference: data.reference,
          plan, billingCycle, amount: String(chargedAmount),
        }));
      }
      window.location.href = data.redirectUrl;
    } else if (data.instructions) {
      alert(data.instructions);
      if (data.pollUrl) {
        startPolling(data.pollUrl, data.reference);
      }
    }
  } catch (err: any) {
    onError(err.message);
  } finally {
    setLoading(false);
  }
};
  // Add polling function
  const startPolling = (pollUrl: string, reference: string) => {
    const interval = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/paynow/poll?pollUrl=${encodeURIComponent(pollUrl)}&plan=${plan}&billingCycle=${billingCycle}&amount=${amount}&reference=${reference}`);
        const data = await res.json();

        if (data.paid) {
          clearInterval(interval);
          onSuccess();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000); // Poll every 3 seconds

    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(interval), 300000);
  };

  // ─── DPO redirect ─────────────────────────────────────────────────────────
  const handleDpoPayment = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/dpo/create-payment', {
        method: 'POST',
        body: JSON.stringify({ amount, plan, billingCycle, email, userId, ...(invoiceId ? { invoiceId } : {}) }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'DPO payment initiation failed');
      window.location.href = data.redirectUrl;
    } catch (err: any) {
      onError(err.message);
      setLoading(false);
    }
  };

  // ─── Bank transfer ────────────────────────────────────────────────────────
  const handleBankTransfer = async () => {
    setBankTransferLoading(true);
    try {
      const res = await apiFetch('/api/bank-transfer/initiate', {
        method: 'POST',
        body: JSON.stringify({ amount, plan, billingCycle, ...(invoiceId ? { invoiceId } : {}) }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to register transfer');
      setBankTransferDone(true);
    } catch (err: any) {
      onError(err.message);
    } finally {
      setBankTransferLoading(false);
    }
  };

  // ─── Styles ───────────────────────────────────────────────────────────────
  const tabStyle = (active: boolean): React.CSSProperties => ({
    flex: 1, padding: '11px 8px',
    background: active ? '#059669' : '#f3f4f6',
    color: active ? 'white' : '#374151',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontWeight: 'bold', fontSize: '13px', transition: 'all 0.2s',
  });

  const cardBrandIcon: Record<string, string> = {
    visa: '💳', mastercard: '💳', amex: '💳', discover: '💳',
  };
  const brandIcon = savedCardBrand ? (cardBrandIcon[savedCardBrand.toLowerCase()] ?? '💳') : '💳';

  return (
    <div>
      {/* Gateway Tabs — only show enabled methods */}
      {amount > 0 && (
        <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
          {methodsEnabled.stripe && (
            <button onClick={() => setSelectedGateway('stripe')} style={tabStyle(selectedGateway === 'stripe')}>
              💳 Card
            </button>
          )}
          {methodsEnabled.paynow && (
            <button onClick={() => setSelectedGateway('paynow')} style={tabStyle(selectedGateway === 'paynow')}>
              🇿🇼 Paynow
            </button>
          )}
          {methodsEnabled.paypal && (
            <button onClick={() => setSelectedGateway('paypal')} style={tabStyle(selectedGateway === 'paypal')}>
              🅿 PayPal
            </button>
          )}
          {methodsEnabled.dpo && (
            <button onClick={() => setSelectedGateway('dpo')} style={tabStyle(selectedGateway === 'dpo')}>
              🌍 Intl Card
            </button>
          )}
          {methodsEnabled.bankTransfer && (
            <button onClick={() => setSelectedGateway('bankTransfer')} style={tabStyle(selectedGateway === 'bankTransfer')}>
              🏦 Bank Transfer
            </button>
          )}
        </div>
      )}

      {/* ── Stripe ── */}
      {selectedGateway === 'stripe' && (
        <div>
          {/* ── Saved card option (only when charging, not saving) ── */}
          {hasSavedCard && (
            <div style={{ marginBottom: '16px' }}>
              {/* Saved card tile */}
              <div
                onClick={() => setUseNewCard(false)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                  border: !useNewCard ? '2px solid #059669' : '1px solid #d1d5db',
                  background: !useNewCard ? '#f0fdf4' : 'white',
                  marginBottom: '10px', transition: 'all 0.15s',
                }}
              >
                <div style={{
                  width: '40px', height: '26px', background: 'linear-gradient(135deg,#1e293b,#0f172a)',
                  borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', flexShrink: 0,
                }}>
                  {brandIcon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937' }}>
                    {savedCardBrand ? savedCardBrand.charAt(0).toUpperCase() + savedCardBrand.slice(1) : 'Card'} ••••{' '}
                    {savedCardLast4 ?? '****'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    Expires {savedCardExpiry ?? '**/**'}
                  </div>
                </div>
                {/* Radio indicator */}
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%',
                  border: !useNewCard ? '5px solid #059669' : '2px solid #d1d5db',
                  flexShrink: 0, transition: 'all 0.15s',
                }} />
              </div>

              {/* Use new card option */}
              <div
                onClick={() => setUseNewCard(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '12px 16px', borderRadius: '12px', cursor: 'pointer',
                  border: useNewCard ? '2px solid #059669' : '1px solid #d1d5db',
                  background: useNewCard ? '#f0fdf4' : 'white',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{
                  width: '40px', height: '26px', borderRadius: '5px',
                  border: '1.5px dashed #d1d5db', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '14px', color: '#9ca3af', flexShrink: 0,
                }}>
                  +
                </div>
                <div style={{ flex: 1, fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                  Use a different card
                </div>
                <div style={{
                  width: '18px', height: '18px', borderRadius: '50%',
                  border: useNewCard ? '5px solid #059669' : '2px solid #d1d5db',
                  flexShrink: 0, transition: 'all 0.15s',
                }} />
              </div>
            </div>
          )}

          {/* New card input (shown when no saved card, or user chose "different card") */}
          {useNewCard && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #d1d5db', borderRadius: '10px', background: '#fafafa' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '10px', color: '#374151' }}>
                Card details
              </label>
              {!stripeReady && <div style={{ color: '#6b7280', fontSize: '13px' }}>Loading secure card form…</div>}
              <div ref={cardMountRef} style={{ minHeight: '22px' }} />
            </div>
          )}

          {cardError && (
            <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px' }}>⚠️ {cardError}</div>
          )}

          <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#065f46' }}>
            🔒 Payments are processed securely by Stripe. Your card details never touch our servers.
          </div>

          <button
            onClick={hasSavedCard && !useNewCard ? handleSavedCardPayment : handleStripePayment}
            disabled={loading || (!hasSavedCard && !stripeReady) || (useNewCard && !stripeReady)}
            style={{
              width: '100%', padding: '14px',
              background: '#059669', color: 'white',
              border: 'none', borderRadius: '10px',
              fontWeight: 'bold', fontSize: '15px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading
              ? 'Processing…'
              : amount === 0
                ? 'Save Card'
                : hasSavedCard && !useNewCard
                  ? `Pay $${amount} with saved card`
                  : `Pay $${amount}`}
          </button>
        </div>
      )}

      {/* ── PayNow ── */}
      {selectedGateway === 'paynow' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            {(['web', 'ecocash', 'onemoney'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setPaynowMethod(m)}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: paynowMethod === m ? '#059669' : '#f3f4f6',
                  color: paynowMethod === m ? 'white' : '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                }}
              >
                {m === 'web' ? '🌐 Paynow' : m === 'ecocash' ? '📱 EcoCash' : '📱 OneMoney'}
              </button>
            ))}
          </div>

          {paynowMethod === 'web' ? (
            <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#1e40af' }}>
              ℹ️ You'll be taken to Paynow's own secure page to complete payment — card, EcoCash, ZimSwitch or any other method they offer. No phone number needed here.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                  {paynowMethod === 'ecocash' ? 'EcoCash' : 'OneMoney'} Number
                </label>
                <input
                  type="tel"
                  value={paynowPhone}
                  onChange={(e) => setPaynowPhone(e.target.value)}
                  placeholder={paynowMethod === 'ecocash' ? '077X XXX XXX' : '078X XXX XXX'}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '10px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  {paynowMethod === 'ecocash'
                    ? 'Enter your EcoCash registered phone number (Econet line)'
                    : 'Enter your OneMoney registered phone number (NetOne line)'}
                </p>
              </div>

              <div style={{ background: '#fefce8', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#713f12' }}>
                ℹ️ You will receive a payment request on your phone. Approve the payment to complete your subscription.
              </div>
            </>
          )}

          <button
            onClick={handlePaynowPayment}
            disabled={loading || (paynowMethod !== 'web' && !paynowPhone.trim())}
            style={{
              width: '100%',
              padding: '14px',
              background: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontWeight: 'bold',
              fontSize: '15px',
              cursor: loading || (paynowMethod !== 'web' && !paynowPhone.trim()) ? 'not-allowed' : 'pointer',
              opacity: loading || (paynowMethod !== 'web' && !paynowPhone.trim()) ? 0.6 : 1
            }}
          >
            {loading
              ? (paynowMethod === 'web' ? 'Redirecting…' : 'Initiating payment…')
              : paynowMethod === 'web'
                ? `Pay $${paynowChargeAmount} on Paynow →`
                : `Pay $${paynowChargeAmount} via ${paynowMethod === 'ecocash' ? 'EcoCash' : 'OneMoney'}`}
          </button>
          {couponCode && paynowChargeAmount !== amount && (
            <p style={{ textAlign: 'center', fontSize: '12px', color: '#059669', marginTop: '8px' }}>
              Coupon {couponCode} applied — ${amount - paynowChargeAmount} off (Paynow only)
            </p>
          )}
        </div>
      )}

      {/* ── PayPal ── */}
      {selectedGateway === 'paypal' && (
        <div>
          <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#1e40af' }}>
            ℹ️ Click the PayPal button below. You can pay with your PayPal balance or any card linked to your PayPal account.
          </div>
          {!paypalReady && <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>Loading PayPal…</div>}
          {loading && <div style={{ textAlign: 'center', padding: '12px', color: '#059669', fontWeight: '600' }}>Processing payment…</div>}
          <div ref={paypalContainerRef} />
        </div>
      )}

      {/* ── DPO International Card ── */}
      {selectedGateway === 'dpo' && (
        <div>
          <div style={{ background: '#eff6ff', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#1e40af' }}>
            ℹ️ You will be redirected to DPO Group's secure page to pay by Visa, Mastercard, or other international cards.
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#065f46' }}>
            🔒 Payment is processed by DPO Group, a PCI-compliant payment provider serving Africa.
          </div>
          <button
            onClick={handleDpoPayment}
            disabled={loading}
            style={{
              width: '100%', padding: '14px',
              background: '#059669', color: 'white',
              border: 'none', borderRadius: '10px',
              fontWeight: 'bold', fontSize: '15px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Redirecting…' : `Pay $${amount} by International Card`}
          </button>
        </div>
      )}

      {/* ── Bank / EcoCash Manual Transfer ── */}
      {selectedGateway === 'bankTransfer' && (
        <div>
          {bankTransferDone ? (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏦</div>
              <div style={{ fontWeight: '700', fontSize: '16px', color: '#1f2937', marginBottom: '8px' }}>Transfer Registered</div>
              <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6 }}>
                Your transfer has been logged. Once we confirm receipt, your plan will be activated — usually within 1–2 business days.
              </div>
            </div>
          ) : (
            <>
              {methodsEnabled.bankTransferDetails && (
                <div style={{ marginBottom: '16px', padding: '14px 16px', border: '1px solid #d1d5db', borderRadius: '10px', background: '#fafafa' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937', marginBottom: '10px' }}>Bank Details</div>
                  {(Object.entries({
                    'Bank Name':      methodsEnabled.bankTransferDetails.bankName,
                    'Account Name':   methodsEnabled.bankTransferDetails.accountName,
                    'Account Number': methodsEnabled.bankTransferDetails.accountNumber,
                    'Branch':         methodsEnabled.bankTransferDetails.branch,
                    'SWIFT / BIC':    methodsEnabled.bankTransferDetails.swiftCode,
                  }) as [string, string][]).filter(([, v]) => v).map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #e5e7eb', fontSize: '13px' }}>
                      <span style={{ color: '#6b7280' }}>{label}</span>
                      <span style={{ fontWeight: '600', color: '#1f2937', userSelect: 'all' }}>{value}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ background: '#fefce8', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#713f12' }}>
                ⚠️ {methodsEnabled.bankTransferDetails?.instructions || 'Reference your invoice number in the transfer description.'}
              </div>
              <button
                onClick={handleBankTransfer}
                disabled={bankTransferLoading}
                style={{
                  width: '100%', padding: '14px',
                  background: '#059669', color: 'white',
                  border: 'none', borderRadius: '10px',
                  fontWeight: 'bold', fontSize: '15px',
                  cursor: bankTransferLoading ? 'not-allowed' : 'pointer',
                  opacity: bankTransferLoading ? 0.6 : 1,
                }}
              >
                {bankTransferLoading ? 'Registering…' : `I've sent the transfer — $${amount}`}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
